/**
 * Placeholder of the empty text field: the same hint a text frame shows. It used to be the Figma
 * card's example sentence, which was meant as sample typed text rather than a placeholder
 * (WMPCM-12967).
 */
export const TEXT_ON_PATH_PLACEHOLDER = 'Type here...'

/**
 * Denominator of the `n / 100` counter while the path has not been measured yet (before the preview
 * lays out, and always in jsdom). Once it has, the counter shows an estimate of how many characters
 * fit along the path (`estimateTextOnPathCapacity`). Display only — not enforced.
 */
export const TEXT_ON_PATH_FALLBACK_CHAR_BUDGET = 100

/**
 * Perpendicular offset a new text-on-path starts at, in pt. Legacy's own 6 (WMPCM-10320 Bug 1).
 *
 * It was 0 while the direction control did not exist, because `dy` pushes the text to one side of
 * the path and WHICH side depends on the shape's winding direction — so a non-zero default landed
 * inside a concave shape as often as outside it, and there was no control to correct that. Both
 * gating controls now exist (Offset in WMPCM-9535, direction in WMPCM-9530), so the condition this
 * was waiting on is met.
 *
 * What forced it is descenders. `<textPath>` puts the BASELINE on the path, so at an offset of 0
 * every `y`, `j` and `p` hangs below the outline and reads as sinking into the shape. That is
 * ordinary typography rather than a layout bug — the descender is *supposed* to sit below the
 * baseline — but with the baseline exactly on the border there is nowhere for it to go. At the
 * default 12pt a descender is roughly 2.5pt deep, so 6pt clears it with room to spare, and the
 * legacy demo's own examples run at 6 and 8 for the same reason.
 *
 * Deliberately NOT solved by subtracting the descent from `dy`: that would silently shift text
 * whenever the point size changed, diverge from legacy's flat offset, and make the Offset control's
 * numbers mean something different from what the user typed.
 */
export const TEXT_ON_PATH_DEFAULT_OFFSET_PT = 6

/** Legacy Page Editor's own default, from its -18…18 pt Offset/Distance preset list — and the live
 *  default again as of WMPCM-10320, so these two are deliberately equal. Kept separately because it
 *  documents where the number comes from. */
export const TEXT_ON_PATH_LEGACY_OFFSET_PT = 6

/**
 * Offset control range and presets (WMPCM-9535, Story 4).
 *
 * `TEXT_ON_PATH_OFFSET_MIN/MAX` match legacy's own typed-input hard range (`setUserTextOnPathOffsetValue`
 * in `odctrl.js` rejects anything outside ±500 with "The number must be between -500 and 500").
 *
 * Three presets labelled Narrow / Natural / Wide, per the Figma control (node `20459:492247`) —
 * NOT legacy's full 19-value `-18…18` list, which was a flat dropdown rather than a chip row. The
 * labels are the same vocabulary Tracking and Kerning already use in this pill, and they are what
 * fixes the values as signed: "Narrow" only means anything if the range runs either side of the
 * path. `Natural` (0, sitting on the path) doubles as the reset target, exactly as it does for
 * those two — which is why there is no separate Reset control here either.
 *
 * Independent of the direction flip (WMPCM-9530): that reverses the direction of travel on every
 * outline, so it is observable at any offset — including `Natural`. An earlier revision expressed
 * the flip on open paths by mirroring this offset, which made `Natural` an exact no-op there; that
 * is no longer how it works. See `resolveTextOnPathDirection`.
 */
export const TEXT_ON_PATH_OFFSET_MIN = -500

export const TEXT_ON_PATH_OFFSET_MAX = 500

export const TEXT_ON_PATH_OFFSET_PRESETS = [
  { label: 'Narrow', value: -TEXT_ON_PATH_LEGACY_OFFSET_PT },
  { label: 'Natural', value: 0 },
  { label: 'Wide', value: TEXT_ON_PATH_LEGACY_OFFSET_PT },
] as const

/**
 * Point size a new text-on-path starts at. 12, not the Type panel's `DEFAULT_POINT_SIZE` of 14 —
 * every artboard in the Figma spec shows `12` in the toolbar's size slot.
 */
export const TEXT_ON_PATH_DEFAULT_POINT_SIZE = 12

/** Screen diameter of the blue start handle, in CSS px. Counter-scaled by canvas zoom when drawn. */
export const TEXT_ON_PATH_DOT_SIZE_PX = 12
